import React, { lazy, Suspense } from 'react';
import { Route, Routes, BrowserRouter as Router, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext.jsx';
import ScrollToTop from '@/components/ScrollToTop.jsx';
import Analytics from '@/components/Analytics.jsx';
import SeoDefaults from '@/components/SeoDefaults.jsx';
import CookieConsent from '@/components/CookieConsent.jsx';
import SiteMetrics from '@/components/SiteMetrics.jsx';

import HomePage from '@/pages/HomePage.jsx';

const JobListingsPage = lazy(() => import('@/pages/JobListingsPage.jsx'));
const JobDetailsPage = lazy(() => import('@/pages/JobDetailsPage.jsx'));
const CVTipsPage = lazy(() => import('@/pages/CVTipsPage.jsx'));
const InterviewPrepPage = lazy(() => import('@/pages/InterviewPrepPage.jsx'));
const GroomingStandardsPage = lazy(() => import('@/pages/GroomingStandardsPage.jsx'));
const HarrodsGuidePage = lazy(() => import('@/pages/HarrodsGuidePage.jsx'));
const SelfridgesGuidePage = lazy(() => import('@/pages/SelfridgesGuidePage.jsx'));
const JobAlertsPage = lazy(() => import('@/pages/JobAlertsPage.jsx'));
const TalentPoolPage = lazy(() => import('@/pages/TalentPoolPage.jsx'));
const CareerFinderPage = lazy(() => import('@/pages/CareerFinderPage.jsx'));
const EarlyCareersPage = lazy(() => import('@/pages/EarlyCareersPage.jsx'));
const AboutPage = lazy(() => import('@/pages/AboutPage.jsx'));
const PartnersPage = lazy(() => import('@/pages/PartnersPage.jsx'));
const ContactPage = lazy(() => import('@/pages/ContactPage.jsx'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage.jsx'));
const InsightsPage = lazy(() => import('@/pages/InsightsPage.jsx'));
const InsightArticlePage = lazy(() => import('@/pages/InsightArticlePage.jsx'));
const VerificationPage = lazy(() => import('@/pages/VerificationPage.jsx'));
const AdminImportsPage = lazy(() => import('@/pages/AdminImportsPage.jsx'));
const AuthPage = lazy(() => import('@/pages/AuthPage.jsx'));

const PageFallback = () => (
  <div className="flex min-h-[45vh] items-center justify-center bg-background text-muted-foreground">
    <p className="text-sm font-medium">Loading page…</p>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <SeoDefaults />
        <Analytics />
        <SiteMetrics />
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/auth" element={<AuthPage />} />
          
          <Route path="/jobs" element={<JobListingsPage />} />
          <Route path="/jobs/:id" element={<JobDetailsPage />} />
          
          {/* Alias /job/:id to /jobs/:id for consistency and compatibility */}
          <Route path="/job/:id" element={<Navigate to="/jobs/:id" replace />} />
          
          <Route path="/career-advice" element={<Navigate to="/insights" replace />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/insights/:slug" element={<InsightArticlePage />} />
          <Route path="/cv-tips" element={<CVTipsPage />} />
          <Route path="/interview-prep" element={<InterviewPrepPage />} />
          <Route path="/grooming-standards" element={<GroomingStandardsPage />} />
          <Route path="/harrods-guide" element={<HarrodsGuidePage />} />
          <Route path="/selfridges-guide" element={<SelfridgesGuidePage />} />
          <Route path="/job-alerts" element={<JobAlertsPage />} />
          <Route path="/talent-pool" element={<TalentPoolPage />} />
          <Route path="/career-finder" element={<CareerFinderPage />} />
          <Route path="/early-careers" element={<EarlyCareersPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/clients" element={<Navigate to="/partners" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Admin Protected Routes */}
          <Route path="/admin/imports" element={<AdminImportsPage />} />

          {/* Diagnostic routes (hidden from main navigation) */}
          <Route path="/verify-live-data" element={<VerificationPage />} />
          
          {/* Catch-all route */}
          <Route path="*" element={
            <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground p-4">
              <h1 className="text-6xl font-bold text-primary mb-4">404</h1>
              <p className="text-xl text-muted-foreground mb-8">This page doesn't exist.</p>
              <a href="/" className="text-primary hover:underline font-medium">Back to Home</a>
            </div>
          } />
        </Routes>
        </Suspense>
        <CookieConsent />
        <Toaster />
      </Router>
    </AuthProvider>
  );
}

export default App;
